import { Icon, Skeleton } from '@capsico/ui';

/*
 * Capsico Money page blocks (W11, inherited pattern AA on Capsico tokens). Presentation only —
 * strings and amounts arrive formatted from the page; data lives in useWallet.
 */

/** Balance hero: Capsico gradient with ink text (never white on yellow). */
export function WalletHero({
  label,
  balance,
  hint,
  loading,
}: {
  label: string;
  balance: string;
  hint: string;
  loading: boolean;
}) {
  return (
    <section className="relative overflow-hidden rounded-sheet bg-linear-to-br from-brand-pale to-brand p-4 text-on-brand">
      <span
        aria-hidden
        className="absolute -top-10 -right-10 size-32 rounded-full bg-surface opacity-20"
      />
      <div className="relative flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{label}</p>
          {loading ? (
            <Skeleton className="mt-1 h-8 w-28" />
          ) : (
            <p className="mt-0.5 text-headline font-bold tabular">{balance}</p>
          )}
          <p className="mt-1 text-caption">{hint}</p>
        </div>
        <span className="inline-flex size-14 shrink-0 items-center justify-center rounded-full bg-surface text-brand-ink">
          <Icon name="wallet" size={26} />
        </span>
      </div>
    </section>
  );
}

/** One live credit: where it came from, what is left, when it expires. */
export function CreditRow({
  title,
  note,
  left,
  expiry,
  soon,
}: {
  title: string;
  note: string | null;
  left: string;
  expiry: string;
  soon: boolean;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
        <Icon name="tag" size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{title}</p>
        {note && <p className="truncate text-caption text-ink-3">{note}</p>}
        <p className={`text-caption tabular ${soon ? 'font-medium text-warning' : 'text-ink-3'}`}>
          {expiry}
        </p>
      </div>
      <span className="shrink-0 text-sm font-semibold text-ink tabular">{left}</span>
    </div>
  );
}

export const LEDGER_ROW_HEIGHT = 68;

/** One ledger line: what happened, order, date and the signed amount. */
export function LedgerRow({
  title,
  sub,
  when,
  amount,
  credit,
}: {
  title: string;
  sub: string | null;
  when: string;
  amount: string;
  credit: boolean;
}) {
  return (
    <div className="flex h-17 items-center gap-3 px-4 shadow-[inset_0_1px_0_var(--color-hair)]">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{title}</p>
        <p className="truncate text-caption text-ink-3 tabular">
          {sub ? `${sub} · ${when}` : when}
        </p>
      </div>
      <span
        className={`shrink-0 text-sm font-semibold tabular ${credit ? 'text-positive' : 'text-ink'}`}
      >
        {amount}
      </span>
    </div>
  );
}
